import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, ExternalLink, BookOpen, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { BOOKS } from '../mock/mock';

const Reader = () => {
  const { bookId } = useParams();
  const { toast } = useToast();
  const book = BOOKS.find((b) => b.id === bookId);

  useEffect(() => {
    if (book && book.pdf) {
      try {
        localStorage.setItem(
          'lr_reading',
          JSON.stringify({ id: book.id, title: book.title, subtitle: book.subtitle, front: book.front, at: Date.now() })
        );
      } catch {
        /* ignore */
      }
    }
  }, [book]);

  const shareBook = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${book.title} — Limpid Reader`, text: `Read "${book.title}" free on Limpid Reader`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast({ title: 'Link copied', description: 'Share this book anywhere.' });
      }
    } catch {
      /* cancelled */
    }
  };

  if (!book || !book.pdf) {
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center">
        <BookOpen className="h-14 w-14 text-brand-goldlight mx-auto mb-5" />
        <h1 className="font-serif text-3xl font-bold text-brand-ink">Book not available</h1>
        <p className="text-brand-brown/70 mt-2">This title isn't available to read online yet.</p>
        <Link to="/read-online">
          <Button className="mt-6 bg-brand-ink hover:bg-brand-brown text-cream rounded-full h-11 px-8">
            Back to Free Library
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-sand/40 min-h-[80vh]">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-4">
            <Link to="/read-online" className="text-brand-brown hover:text-brand-brass transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="font-serif text-2xl font-bold text-brand-ink leading-tight">{book.title}</h1>
              <p className="text-sm text-brand-brass italic">{book.subtitle}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={shareBook} variant="outline" className="border-brand-gold text-brand-ink hover:bg-brand-gold hover:text-white rounded-full gap-2">
              <Share2 className="h-4 w-4" /> Share
            </Button>
            <a href={book.pdf} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-brand-gold text-brand-ink hover:bg-brand-gold hover:text-white rounded-full gap-2">
                <ExternalLink className="h-4 w-4" /> Open in new tab
              </Button>
            </a>
            <a href={book.pdf} download target="_blank" rel="noopener noreferrer">
              <Button className="bg-brand-ink hover:bg-brand-brown text-cream rounded-full gap-2">
                <Download className="h-4 w-4" /> Download PDF
              </Button>
            </a>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-brand-goldlight/40">
          <iframe
            title={book.title}
            src={`https://docs.google.com/viewer?url=${book.pdf}&embedded=true`}
            className="w-full"
            style={{ height: '80vh', border: 'none' }}
          />
        </div>

        <p className="text-center text-xs text-brand-brown/60 mt-4">
          Reading free courtesy of the author. Prefer a printed copy?{' '}
          <Link to="/buy" className="text-brand-brass underline">Order the physical edition</Link>.
        </p>
      </div>
    </div>
  );
};

export default Reader;
